<template>
  <div class="bf-card bf-recent">
    <div class="bf-recent__head">
      <div>
        <div class="bf-recent__title">Últimas Integrações</div>
        <div class="bf-recent__subtitle">Execuções mais recentes no período</div>
      </div>

      <router-link class="bf-recent__link" :to="{ name: 'ExecutionPage' }">
        Ver histórico completo
      </router-link>
    </div>

    <v-data-table
      class="bf-table"
      :headers="columns"
      :items="executions"
      :loading="isLoading"
      :items-per-page="size"
      hide-default-footer
    >
      <template v-slot:[`item.status`]="{ value }">
        <span
          v-if="value == 'success' || value == 'Sucesso'"
          class="bf-pill bf-pill--ok"
        >
          Sucesso
        </span>
        <span
          v-else-if="value == 'error' || value == 'Erro'"
          class="bf-pill bf-pill--err"
        >
          Erro
        </span>
        <span
          v-else-if="value == 'processing' || value == 'Processando'"
          class="bf-pill bf-pill--info"
        >
          Processando
        </span>
        <span
          v-else-if="
            value == 'waiting-process' || value == 'Aguardando Processamento'
          "
          class="bf-pill bf-pill--info"
        >
          Aguardando Processamento
        </span>
        <span v-else class="bf-pill bf-pill--mut"> Não Executado </span>
      </template>

      <template v-slot:no-data> Não há execuções no período </template>

      <template v-slot:loading>
        <v-skeleton-loader type="table-row@5"></v-skeleton-loader>
      </template>
    </v-data-table>
  </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref, watch } from "vue";

import { useDashboardStore } from "@/stores/dashboard.store";
import { useExecution } from "@/composables/useExecution";

export default defineComponent({
  name: "RecentExecutions",
  setup() {
    const dashboardStore = useDashboardStore();

    const { columns, executions, getExecutions } = useExecution();

    const isLoading = ref(false);
    const size = 5;

    const fetchRecent = async () => {
      // Só busca quando o período já foi definido pelo DashboardPage.
      // Evita um fetch inicial com datas vazias (default = últimas 24h) que
      // poderia sobrescrever, por corrida, o resultado do período correto.
      const { initial_datetime, final_datetime } = dashboardStore.filters;
      if (!initial_datetime || !final_datetime) return;

      isLoading.value = true;
      try {
        await getExecutions({
          page: 1,
          size,
          initial_datetime,
          final_datetime,
          sort_type: false,
        });
      } catch (error) {
        console.error("Erro ao buscar últimas integrações:", error);
      } finally {
        isLoading.value = false;
      }
    };

    onMounted(fetchRecent);

    watch(() => dashboardStore.filters, fetchRecent, { deep: true });

    return {
      columns,
      executions,
      isLoading,
      size,
    };
  },
});
</script>

<style lang="scss" scoped>
.bf-recent {
  margin-top: 16px;
  padding: 0;
  overflow: hidden;
}

.bf-recent__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 18px 22px 14px;
}

.bf-recent__title {
  font-size: 14px;
  font-weight: 700;
  color: var(--text);
}

.bf-recent__subtitle {
  font-size: 11.5px;
  color: var(--text3);
  margin-top: 2px;
}

.bf-recent__link {
  font-size: 12.5px;
  font-weight: 600;
  color: var(--brand2);
  text-decoration: none;
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
  }
}

.bf-table {
  background: transparent;
  color: var(--text);

  :deep(.v-table__wrapper) {
    background: transparent;
  }

  :deep(thead th) {
    background: var(--surface2) !important;
    color: var(--text3) !important;
    font-size: 10.5px !important;
    font-weight: 700 !important;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    border-bottom: 1px solid var(--border) !important;
  }

  :deep(tbody td) {
    font-size: 12.5px;
    color: var(--text2);
    border-bottom: 1px solid var(--border) !important;
  }

  :deep(tbody tr:hover td) {
    background: var(--hover);
  }
}
</style>
